import * as React from 'react';
import { lazy, Suspense, useEffect, useState } from 'react';

import * as analytics from '@opendoor/shared-fe/build/analytics';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { Box, Loader } from '@opendoor/bricks/core';
import { NamelessAccountContextProvider } from '@opendoor/cinderblocks/growth/auth/NamelessAccountContext';

import { getHuman_web_userSession_human } from 'src/api/queries/__generated__/getHuman';
import { ColumnContainer, Layout } from 'src/components/layouts/TwoColumnPageLayout';
import { buildAccountCreationAnswers } from 'src/helpers/answers';
import { BASE_URL } from 'src/helpers/globals';
import { Session } from 'src/helpers/session';
import { usePromptGoogleOneTap } from 'src/helpers/usePromptGoogleOneTap';
import { AppDispatch } from 'src/store';
import { useGetHumanQuery, useLazyValidateEmailQuery } from 'src/store/api';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getCurrentSlug } from 'src/store/router/selectors';
import {
  handleException,
  sendPasswordSetUpEmail as sendPasswordSetUpEmailAction,
} from 'src/store/sellerFlow/actions';
import { AnswerSet } from 'src/store/sellerFlow/AnswerSet';
import {
  answersIsSubmitting as getAnswersIsSubmitting,
  leadToken as getLeadToken,
  submittedByAgent as getSubmittedByAgent,
  isPhoneNumberRequired,
} from 'src/store/sellerFlow/selectors';
import { changeAnswers, SavedAnswer } from 'src/store/sellerInput';
import {
  submitAndGoToNextPage as uiSubmitAndGoToNextPage,
  submitAnswers as uiSubmitAnswers,
} from 'src/store/ui';

import { AccountConfirmation } from './AccountConfirmation';
import AccountEmailInput from './AccountEmailInput';
import AccountNameInput from './AccountNameInput';
import AccountPasswordInput from './AccountPasswordInput';
import AccountPhoneInput from './AccountPhoneInput';
import { BottomBar } from './BottomBar';
import { InContainer } from './CrossFadeAnimation';
import FlowPageHeader from './FlowPageHeader';
import Navigation from './Navigation';
import { ProgressIndicator } from './ProgressIndicator';

const AccountForm = lazy(() => import('@opendoor/cinderblocks/platform/auth/AccountForm'));

const standardTcpaDisclaimerText = ({ actionText }: { actionText: string }) =>
  `By clicking "${actionText}" I agree that Opendoor may call or text me about my inquiry, including by automated means. I may opt out at any time, and consent is not a condition of using Opendoor's services.`;

export let accountCreationTitle = 'See your estimated offer right away';
let accountCreationSubtitle = 'Sign in or enter your email address.';

export const submitChangedAnswers = (
  dispatch: AppDispatch,
  answers: AnswerSet,
  shouldGoToNextPage: boolean,
) => {
  analytics.trackEvent('flow-slug', 'change', 'account-submit-changed-answers', {
    answers,
    shouldGoToNextPage,
  });
  const keys = Object.keys(answers) ?? [];

  // TODO: find a way to type this without needing to `as`
  // this is safe to do right now since we only call this fn with a set
  // of predefined values
  const newAnswers = keys.map((key) => ({ key, answer: answers[key] })) as Array<{
    key: string;
    answer: SavedAnswer;
  }>;
  dispatch(changeAnswers(newAnswers));

  if (shouldGoToNextPage) {
    dispatch(uiSubmitAndGoToNextPage({ additionalAnswerKeysToSubmit: keys }));
  } else {
    dispatch(uiSubmitAnswers({ additionalAnswerKeysToSubmit: keys }));
  }
};

function humanHasEmail(
  human: getHuman_web_userSession_human | null | undefined,
): human is getHuman_web_userSession_human & { email: string } {
  return human?.email != null;
}

const AccountPage: React.FC = () => {
  const [validateEmail] = useLazyValidateEmailQuery();
  const [hasGeneratedPassword, setHasGeneratedPassword] = React.useState(false);
  const [isNewHuman, setIsNewHuman] = React.useState(true);
  const [emailLoading, setEmailLoading] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [passwordLoading, setPasswordLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  const [emailRegistrationError, setEmailRegistrationError] = React.useState<string | undefined>();
  const answersIsSubmitting = useAppSelector(getAnswersIsSubmitting);
  const sellerInputUuid = useAppSelector(getLeadToken);
  const currentSlug = useAppSelector(getCurrentSlug);

  const { data: human } = useGetHumanQuery();

  const [emailFilled, setEmailFilled] = React.useState(false);
  const phoneNumberRequired = useAppSelector(isPhoneNumberRequired);

  const submittedByAgent = useAppSelector(getSubmittedByAgent);
  const registrationHeaderText = `You're all set! ${
    submittedByAgent ? "Your client's" : 'Your'
  } offer is ready to go.`;
  const registrationSubHeaderText = `Add your info so we know who ${
    submittedByAgent ? 'you are.' : "it's for."
  }`;

  const [needsToLogin, setNeedsToLogin] = useState(false);
  const [hidePasswordField, setHidePasswordField] = useState(true);
  useEffect(() => {
    if (hidePasswordField && needsToLogin) {
      // Shouldn't hide password field for existing user who needs to login
      setHidePasswordField(false);
    }
    // eslint-disable-next-line
  }, [needsToLogin]);

  useEffect(() => {
    if (emailRegistrationError) {
      showToast(
        'error',
        'Unable to register your account. Please try again.',
        'reception-fe-nameless-register-error-toast',
        {
          toastOptions: { autoClose: 7000 },
        },
      );
    }
  }, [emailRegistrationError]);

  usePromptGoogleOneTap((response) => {
    onRegister(
      undefined, // email
      undefined, // name
      undefined, // password
      undefined, // phone
      response.credential, // googleAuthId
    );
  });

  const dispatch = useAppDispatch();

  const sendPasswordSetUpEmail = (email: string) => {
    const redirectUrl = `${BASE_URL}/seller-lead/${sellerInputUuid}/${currentSlug}`;
    dispatch(sendPasswordSetUpEmailAction(email, redirectUrl));
  };

  const onLogin = (email: string, password: string) => {
    analytics.trackEvent('flow-slug', 'change', 'account-on-login', {
      currentSlug,
      seller_input_uuid: sellerInputUuid,
    });

    setPasswordLoading(true);
    Session.logIn(email, password)
      .then((data) => {
        return Promise.resolve(updateSellerDetails(data.full_name, data.email, data.primary_phone));
      })
      .catch((err) => {
        dispatch(handleException(err, 'sign-in', 'warn'));
        setError(err.toString());
        setPasswordLoading(false);
      });
  };

  const onRequestResetPassword = (email: string) => {
    Session.requestResetPassword(email);
  };

  const onAccountUpdate = (name: string, phone?: string) => {
    try {
      setPasswordLoading(true);
      updateSellerDetails(name, email, phone);
    } catch (err) {
      dispatch(handleException(err, 'update-seller-details', 'warn'));
      setError(err.toString());
    } finally {
      setPasswordLoading(false);
    }
  };

  const onRegister = (
    email?: string,
    name?: string,
    // we no longer capture a password here, but this parameter is still passed
    // in on calls to onRegister
    _password?: string,
    phone?: string,
    googleAuthId?: string,
    facebookAuthId?: string,
    passwordlessToken?: string,
    hasGeneratedPassword?: boolean,
  ) => {
    if (googleAuthId || facebookAuthId) {
      setEmailLoading(true);
    } else {
      setPasswordLoading(true);
    }

    hasGeneratedPassword = true;
    Session.register(
      email,
      name,
      undefined,
      phone,
      googleAuthId,
      facebookAuthId,
      passwordlessToken,
      hasGeneratedPassword,
    )
      .then((data) => {
        return Promise.resolve(updateSellerDetails(data.full_name, data.email, data.primary_phone));
      })
      .catch((err) => {
        dispatch(handleException(err, 'register', 'warn'));
        const error = err.toString();
        setError(error);
        if (error === 'Error: 422 ') {
          // user already exists with email, prompt to log in
          if (email) {
            updateSellerEmail(email);
            setHidePasswordField(false);
            setEmail(email);
          }
          setIsNewHuman(false);
        }
        setPasswordLoading(false);
        setEmailLoading(false);
      });
  };

  const onNamelessHumanStatusRequest = (email: string) => {
    setEmailLoading(true);
    Session.requestHumanStatus(email)
      .then((data) => {
        // Human already exists with email, show login
        setNeedsToLogin(true);
        setIsNewHuman(false);
        const { has_generated_password } = data;
        setHasGeneratedPassword(!!has_generated_password);
        setEmailLoading(false);
        if (has_generated_password) {
          sendPasswordSetUpEmail(email);
        }
      })
      .catch((exception) => {
        // No human exists with email, show account sign up
        dispatch(handleException(exception, 'human-status-request', 'warn'));
        setIsNewHuman(true);
        setNeedsToLogin(false);
        setEmailRegistrationError('');

        Session.register(
          email,
          'Customer',
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          true,
        )
          .then(() => {
            setEmail(email);
            return Promise.resolve(
              submitChangedAnswers(dispatch, buildAccountCreationAnswers('Customer', email), false),
            );
          })
          .catch(() => {
            setEmailRegistrationError('Registration failed. Please try again.');
            setEmailLoading(false);
          });
        setEmailLoading(false);
      });
  };

  const updateSellerEmail = (email: string) => {
    setEmailFilled(true);
    submitChangedAnswers(
      dispatch,
      {
        'seller.email': email,
      },
      false,
    );
  };

  const updateSellerDetails = (
    name: string,
    email: string,
    phone?: string,
    extraAnswers?: AnswerSet,
  ) => {
    const answersToSubmit = buildAccountCreationAnswers(name, email, phone);
    const mergedAnswers: AnswerSet = { ...answersToSubmit, ...extraAnswers };
    submitChangedAnswers(dispatch, mergedAnswers, true);
    setPasswordLoading(false);
  };

  const onValidateEmail = async (
    email: string,
  ): Promise<{ suggestion: string; valid: boolean }> => {
    if (email.indexOf('@opendoorqa.com') > 0) {
      // Don't run validation on smoke test leads, don't waste money
      return Promise.resolve({ suggestion: '', valid: true });
    }

    return validateEmail(email).then((res) => {
      return res.data ? res.data : { suggestion: '', valid: true };
    });
  };

  let onboardingAccountPageCTA = true;
  if (currentSlug == 'account-sb') {
    accountCreationTitle = 'Just browsing? See some numbers right away';
    accountCreationSubtitle =
      'Sign in so we can share an estimated range before you move forward. There’s no obligation to work with us.';
    onboardingAccountPageCTA = false;
  }

  const showNovoStyle = useAppSelector((state) => state.featureFlags.novoAuthStyle);

  return (
    <Layout showFooter={false}>
      {humanHasEmail(human) ? (
        <AccountConfirmation
          title={accountCreationTitle}
          subtitle={accountCreationSubtitle}
          human={human}
        />
      ) : (
        <ColumnContainer>
          <Box flex={1} position="relative">
            <InContainer>
              <FlowPageHeader
                title={!emailLoading && emailFilled ? registrationHeaderText : accountCreationTitle}
                subtitle={
                  !emailLoading && emailFilled ? registrationSubHeaderText : accountCreationSubtitle
                }
              />
            </InContainer>
          </Box>

          <Box flex={1} position="relative">
            <InContainer>
              <Suspense fallback={<Loader />}>
                <NamelessAccountContextProvider
                  shouldRegisterOnEmailSubmit={true}
                  onAccountUpdate={onAccountUpdate}
                  emailRegistrationError={emailRegistrationError}
                >
                  <AccountForm
                    showNovoStyle={showNovoStyle}
                    analyticsName="reception-fe-account-form"
                    loginHeaderText=""
                    loginSubHeaderText=""
                    onHumanStatusRequest={onNamelessHumanStatusRequest}
                    onLogin={onLogin}
                    onRegister={onRegister}
                    email={email}
                    emailLoading={emailLoading}
                    passwordLoading={passwordLoading || answersIsSubmitting}
                    hasGeneratedPassword={hasGeneratedPassword}
                    isNewHuman={isNewHuman}
                    error={error}
                    showGoogleAuth
                    phoneNumberInputOptional={!phoneNumberRequired}
                    openForgotPasswordInNewTab
                    customEmailValidityCheck={onValidateEmail}
                    customEmailValidityCheckDelay={500}
                    requestResetPassword={onRequestResetPassword}
                    containerProps={{ maxWidth: '100%', padding: 0 }}
                    hidePasswordField={hidePasswordField}
                    disclaimerProps={{
                      location: 'bottom',
                      formatText: standardTcpaDisclaimerText,
                    }}
                    registrationHeaderText=""
                    registrationSubHeaderText=""
                    onboardingAccountPageCTA={onboardingAccountPageCTA}
                    fullNamePlaceholder={submittedByAgent ? 'John Smith' : 'Jasper Smith'}
                    fullNameLabel={submittedByAgent ? 'Full name of agent' : 'Full name'}
                    EmailInput={AccountEmailInput}
                    PasswordInput={AccountPasswordInput}
                    NameInput={AccountNameInput}
                    PhoneInput={AccountPhoneInput}
                    unsetContainer={true}
                  />
                </NamelessAccountContextProvider>
              </Suspense>
            </InContainer>
          </Box>
        </ColumnContainer>
      )}

      <Box data-testid="reception-footer" mt="48px" mb={['140px', null, null, '156px']}></Box>

      <BottomBar>
        <ProgressIndicator />
        <Navigation showNextButton={false} />
      </BottomBar>
    </Layout>
  );
};

export default AccountPage;
